import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getAllExams, 
  getQuizBuilderSubjects,
  generateCustomQuiz,
  isSuccessResponse,
  getResponseData,
  getErrorMessage,
  getUserSubscriptionFeatures
} from '../components/apiClient.js';
import { 
  QuizPreviewSummary, 
  QuizGenerationProgress, 
  NumberOfQuestions, 
  GenerateButton, 
  ExamSelection, 
  PointsWarningModal, 
  ValidationAlert, 
  PointsPolicyBanner, 
  QuotaBanner 
} from '../components/custom-quiz';
import { QuizOption, QuizPreview } from '../types';

interface PYQQuizConfig {
  exam: string;
  subjects: string[];
  numberOfQuestions: number;
}

const PYQQuizGenerator: React.FC = () => {
  const navigate = useNavigate();
  
  const [quizConfig, setQuizConfig] = useState<PYQQuizConfig>({
    exam: '',
    subjects: [],
    numberOfQuestions: 20
  });

  const [isQuizGenerated, setIsQuizGenerated] = useState(false);
  const [quizPreview, setQuizPreview] = useState<QuizPreview | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // API Data States
  const [examOptions, setExamOptions] = useState<QuizOption[]>([]);
  const [subjectOptions, setSubjectOptions] = useState<QuizOption[]>([]);
  
  // Loading States
  const [loadingExams, setLoadingExams] = useState(true);
  const [loadingSubjects, setLoadingSubjects] = useState(true);

  // Separate state for number input to allow proper editing
  const [numberOfQuestionsInput, setNumberOfQuestionsInput] = useState<string>('20');

  // Points warning modal state
  const [showPointsWarning, setShowPointsWarning] = useState(false);

  // Validation alert state
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  // Subscription state
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  // Exam-Subject Mapping Configuration
  const examSubjectMapping: Record<string, string[]> = {
    '1': ['1', '4', '5'],
    '2': ['1', '4', '5'],
    '3': ['1', '2', '3','5'],
  };

  // Handle exam change and reset subjects
  const handleExamChange = (examId: string) => {
    setQuizConfig(prev => ({
      ...prev,
      exam: examId,
      subjects: [] // Reset subjects when exam changes
    }));
  };

  const loadExams = async () => {
    try {
      setLoadingExams(true);
      const response = await getAllExams();
      if (isSuccessResponse(response)) {
        const exams = getResponseData(response);
        setExamOptions(exams.map((exam: any) => ({
          value: exam.id.toString(),
          label: exam.exam_name,
          id: exam.id
        })));
      } else {
        console.error('Failed to load exams:', getErrorMessage(response));
      }
    } catch (error) {
      console.error('Error loading exams:', error);
    } finally {
      setLoadingExams(false);
    }
  };

  const loadSubjects = async () => {
    try {
      setLoadingSubjects(true);
      const response = await getQuizBuilderSubjects();
      if (isSuccessResponse(response)) {
        const subjects = getResponseData(response);
        setSubjectOptions(subjects.map((subject: any) => ({
          value: subject.id.toString(),
          label: `${subject.subject}`,
          id: subject.id
        })));
      } else {
        console.error('Failed to load subjects:', getErrorMessage(response));
      }
    } catch (error) {
      console.error('Error loading subjects:', error);
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Load initial data
  useEffect(() => {
    loadExams();
    loadSubjects();
  }, []);

  // Load subscription data
  useEffect(() => {
    const loadSubscription = async () => {
      try {
        setLoadingSubscription(true);
        const response = await getUserSubscriptionFeatures();
        if (response && response.result) {
          setSubscriptionData(response.result);
        }
      } catch (error) {
        console.error('Failed to load subscription data:', error);
      } finally {
        setLoadingSubscription(false);
      }
    };
    loadSubscription();
  }, []);

  const handleSubjectChange = (subject: string) => {
    const isSubjectSelected = quizConfig.subjects.includes(subject);
    
    setQuizConfig(prev => ({
      ...prev,
      subjects: isSubjectSelected
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject]
    }));
  };

  const generateQuiz = async () => {
    // Check quota first
    const customQuiz = subscriptionData?.features?.custom_quiz;
    if (customQuiz && customQuiz.quota_exceeded) {
      const planKey = subscriptionData.subscription_plan_key?.toLowerCase();
      const upgradeMessage = planKey === 'free' 
        ? 'You have reached your custom quiz limit. Please upgrade to Basic, Premium, or Pro plan for unlimited quizzes.'
        : 'You have reached your custom quiz limit.';
      setValidationMessage(upgradeMessage);
      setShowValidationAlert(true);
      return;
    }

    if (!quizConfig.exam || quizConfig.subjects.length === 0) {
      setValidationMessage('Please fill in all required fields: Exam and Subjects');
      setShowValidationAlert(true);
      return;
    }

    // Check if user has less than 20 questions and show warning modal
    if (quizConfig.numberOfQuestions < 20) {
      setShowPointsWarning(true);
      return;
    }

    await proceedWithQuizGeneration();
  };

  const proceedWithQuizGeneration = async () => {
    setIsGenerating(true);
    
    // Scroll to top of page to show progress display
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    try {
      console.log('Current PYQ quiz config:', quizConfig);
      
      // Prepare config with fixed Advance difficulty and pyq question_type
      const apiConfig = {
        exam: quizConfig.exam,
        subjects: quizConfig.subjects,
        chapters: [],
        topics: [],
        difficulty: 'Advance', // Fixed to Advance
        numberOfQuestions: quizConfig.numberOfQuestions,
        question_type: 'pyq' // Set to PYQ type
      };
      
      // Call the API
      const response = await generateCustomQuiz(apiConfig);
      
      console.log('PYQ quiz generation response:', response);
      
      if (isSuccessResponse(response)) {
        const quizData = getResponseData(response);
        
        console.log('Full PYQ quiz response data:', quizData);
        console.log('quiz_reference from response:', quizData.quiz_reference);
        
        const selectedExam = examOptions.find(e => e.value === quizConfig.exam);
        const selectedSubjects = subjectOptions.filter(s => quizConfig.subjects.includes(s.value));
        
        const description = `Previous Year Questions (PYQ) covering ${selectedSubjects.map(s => s.label).join(', ')} - Advanced Level`;
        
        setQuizPreview({
          title: quizData.quiz_name || `${selectedExam?.label} PYQ Quiz`,
          description: description,
          estimatedTime: quizData.estimated_time || `${Math.ceil(quizConfig.numberOfQuestions * 2)} minutes`,
          totalMarks: quizData.total_marks || (quizConfig.numberOfQuestions * 4),
          quizReference: quizData.quiz_reference,
          actualQuestions: quizData.total_questions
        });
        
        console.log('Set quizPreview with quizReference:', quizData.quiz_reference);
        
        setIsQuizGenerated(true);
        
        // Refresh subscription data after successful quiz generation
        try {
          const subscriptionResponse = await getUserSubscriptionFeatures();
          if (subscriptionResponse && subscriptionResponse.data) {
            setSubscriptionData(subscriptionResponse.data);
          }
        } catch (error) {
          console.error('Failed to refresh subscription data:', error);
        }
      } else {
        const errorMsg = getErrorMessage(response) || 'Failed to generate PYQ quiz';
        console.error('PYQ quiz generation failed:', response);
        alert(`Error: ${errorMsg}`);
      }
    } catch (error) {
      console.error('PYQ quiz generation error:', error);
      alert('An error occurred while generating the quiz. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Modal handlers
  const handleSetTo20Questions = () => {
    setQuizConfig(prev => ({ ...prev, numberOfQuestions: 20 }));
    setNumberOfQuestionsInput('20');
    setShowPointsWarning(false);
    proceedWithQuizGeneration();
  };

  const handleContinueAnyway = () => {
    setShowPointsWarning(false);
    proceedWithQuizGeneration();
  };

  const startQuiz = () => {
    console.log('Starting PYQ quiz with quizReference:', quizPreview?.quizReference);
    if (quizPreview?.quizReference) {
      navigate(`/quiz/${quizPreview.quizReference}`);
    } else {
      console.error('No quiz reference available for navigation');
    }
  };

  const resetForm = () => {
    setQuizConfig({
      exam: '',
      subjects: [],
      numberOfQuestions: 20
    });
    setNumberOfQuestionsInput('20');
    setIsQuizGenerated(false);
    setQuizPreview(null);
  };

  // Get filtered subjects based on selected exam
  const getFilteredSubjects = () => {
    if (!quizConfig.exam) return [];
    
    const allowedSubjectIds = examSubjectMapping[quizConfig.exam] || [];
    if (allowedSubjectIds.length === 0) {
      return subjectOptions;
    }
    
    return subjectOptions.filter(subject => 
      allowedSubjectIds.includes(subject.value)
    );
  };

  const filteredSubjects = getFilteredSubjects();

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '0px' }}>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            {/* Header */}
            <div className="text-center mb-5">
              <h1 className="display-4 fw-bold mb-3">
                <i className="fas fa-history me-3" style={{ color: '#667eea' }}></i>
                Previous Year Questions (PYQ)
              </h1>
              <p className="lead text-muted">
                Practice with real exam questions from previous years - Advanced Level
              </p>
            </div>

            {/* Points Policy Banner */}
            <PointsPolicyBanner />

            {/* Quota Banner */}
            <QuotaBanner subscriptionData={subscriptionData} loading={loadingSubscription} />

            <div className="row g-4">
              {/* Progress Display while generating */}
              <QuizGenerationProgress isGenerating={isGenerating} />

              {/* Quiz Configuration Form - Only show when not generated and not generating */}
              {!isQuizGenerated && !isGenerating && (
                <div className="col-12">
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                    <div className="card-header bg-transparent border-0 p-4">
                      <h4 className="fw-bold mb-0">
                        <i className="fas fa-sliders-h me-2 text-primary"></i>
                        PYQ Quiz Configuration
                      </h4>
                      <p className="text-muted mb-0 mt-2">
                        <i className="fas fa-info-circle me-2"></i>
                        All PYQ quizzes are automatically set to <strong>Advanced</strong> difficulty level
                      </p>
                    </div>
                    <div className="card-body p-4">
                      <form>
                        {/* Exam Selection */}
                        <ExamSelection
                          selectedExam={quizConfig.exam}
                          examOptions={examOptions}
                          loadingExams={loadingExams}
                          onExamChange={handleExamChange}
                        />

                        {/* Show subject selection only after exam selection */}
                        {quizConfig.exam && (
                          <>
                            {/* Subject Selection - Simplified (No Chapters/Topics) */}
                            <div className="mb-4">
                              <label className="form-label fw-bold">
                                <i className="fas fa-book me-2 text-primary"></i>
                                Select Subjects
                                <span className="text-danger ms-1">*</span>
                              </label>
                              {loadingSubjects ? (
                                <div className="text-center py-3">
                                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Loading subjects...</span>
                                  </div>
                                  <p className="text-muted mt-2 mb-0">Loading subjects...</p>
                                </div>
                              ) : (
                                <div className="card border-0" style={{ backgroundColor: '#f8f9ff' }}>
                                  <div className="card-body p-3">
                                    {filteredSubjects.length === 0 ? (
                                      <p className="text-muted mb-0">
                                        <i className="fas fa-info-circle me-2"></i>
                                        No subjects available for the selected exam
                                      </p>
                                    ) : (
                                      <div className="row g-2">
                                        {filteredSubjects.map((subject) => (
                                          <div key={subject.value} className="col-md-6">
                                            <div 
                                              className={`card h-100 border-0 shadow-sm cursor-pointer transition-all ${
                                                quizConfig.subjects.includes(subject.value)
                                                  ? 'border-primary bg-primary bg-opacity-10'
                                                  : 'bg-white hover-shadow'
                                              }`}
                                              onClick={() => handleSubjectChange(subject.value)}
                                              style={{ 
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                border: quizConfig.subjects.includes(subject.value) ? '2px solid #667eea' : '1px solid #dee2e6'
                                              }}
                                            >
                                              <div className="card-body p-3 d-flex align-items-center">
                                                <div className="form-check mb-0 me-3">
                                                  <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id={`subject-${subject.value}`}
                                                    checked={quizConfig.subjects.includes(subject.value)}
                                                    onChange={() => handleSubjectChange(subject.value)}
                                                    style={{ 
                                                      cursor: 'pointer',
                                                      width: '20px',
                                                      height: '20px',
                                                      marginTop: '0'
                                                    }}
                                                  />
                                                </div>
                                                <label 
                                                  className="form-check-label flex-grow-1 mb-0" 
                                                  htmlFor={`subject-${subject.value}`}
                                                  style={{ cursor: 'pointer' }}
                                                >
                                                  <strong>{subject.label}</strong>
                                                </label>
                                                {quizConfig.subjects.includes(subject.value) && (
                                                  <i className="fas fa-check-circle text-primary"></i>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              <small className="text-muted">
                                <i className="fas fa-lightbulb me-1"></i>
                                Select one or more subjects for your PYQ quiz
                              </small>
                            </div>

                            {/* Number of Questions */}
                            <NumberOfQuestions
                              numberOfQuestionsInput={numberOfQuestionsInput}
                              onInputChange={(value) => setNumberOfQuestionsInput(value)}
                              onBlur={(value) => {
                                const numValue = parseInt(value) || 1;
                                const validValue = Math.max(1, Math.min(60, numValue));
                                setNumberOfQuestionsInput(validValue.toString());
                                setQuizConfig(prev => ({ 
                                  ...prev, 
                                  numberOfQuestions: validValue 
                                }));
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.currentTarget.blur();
                                }
                              }}
                            />

                            {/* Generate Button */}
                            <GenerateButton
                              isGenerating={isGenerating}
                              onGenerate={generateQuiz}
                              quotaExceeded={subscriptionData?.features?.custom_quiz?.quota_exceeded || false}
                              label="Generate PYQ Quiz"
                            />
                          </>
                        )}
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quiz Preview/Summary - Adapted for PYQ */}
            {(isQuizGenerated || isGenerating) && (
              <QuizPreviewSummary
                isQuizGenerated={isQuizGenerated}
                isGenerating={isGenerating}
                quizPreview={quizPreview}
                quizConfig={{
                  exam: quizConfig.exam,
                  subjects: quizConfig.subjects,
                  chapters: [],
                  topics: [],
                  difficulty: 'Advance',
                  numberOfQuestions: quizConfig.numberOfQuestions
                }}
                examOptions={examOptions}
                subjectOptions={subjectOptions}
                chapterOptions={[]}
                topicOptions={[]}
                difficultyOptions={[{ value: 'Advance', label: 'Hard', color: '#dc3545' }]}
                onStartQuiz={startQuiz}
                onResetForm={resetForm}
              />
            )}
          </div>
        </div>
      </div>

      {/* Points Warning Modal */}
      <PointsWarningModal
        isOpen={showPointsWarning}
        currentQuestions={quizConfig.numberOfQuestions}
        onClose={() => setShowPointsWarning(false)}
        onSetTo20Questions={handleSetTo20Questions}
        onContinueAnyway={handleContinueAnyway}
      />

      <ValidationAlert
        isOpen={showValidationAlert}
        message={validationMessage}
        onClose={() => setShowValidationAlert(false)}
      />
    </div>
  );
};

export default PYQQuizGenerator;
